import { provideHttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, inject, LOCALE_ID } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { provideApiBaseUrl } from "./core/http/api-request.provider";
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { DatePipe, registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';
import fr from '@angular/common/locales/fr';
import pt from '@angular/common/locales/pt';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'D',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id   : 'en',
                        label: 'English',
                    },
                    {
                        id   : 'es',
                        label: 'Español',
                    },
                    {
                        id   : 'fr',
                        label: 'Français',
                    },
                    {
                        id   : 'it',
                        label: 'Italiano',
                    },
                    {
                        id   : 'pt',
                        label: 'Português',
                    },
                ],
                defaultLang         : 'en',
                fallbackLang        : 'en',
                reRenderOnLangChange: true,
                prodMode            : true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const browserLang = navigator.language.split("-")[0];

                let lang = translocoService.getDefaultLang();

                // TODO from translocoService.getAvailableLangs()
                if (browserLang == 'es' || browserLang == 'en' || browserLang == 'fr' || browserLang == 'it' || browserLang == 'pt') {
                    lang = browserLang;
                }

                translocoService.setActiveLang(lang);

                return () => firstValueFrom(translocoService.load(lang));
            },
            multi: true,
        },
        // Transloco locales
        provideTranslocoLocale({
            langToLocaleMapping: {
                en: 'en-GB',
                es: 'es-ES',
                fr: 'fr-FR',
                pt: 'pt-PT'
            }
        }),
        provideTranslocoMessageformat(),
        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout : 'classic-doorwifi',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme  : 'theme-doorwifi',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                    {
                        id  : 'theme-doorwifi',
                        name: 'Doorwifi',
                    }
                ],
            },
        }),

        // Custom HTTP interceptors
        // Register after fuse MockAPI interceptor otherwise it wouldn't be able to match the routes.
        provideApiBaseUrl(),

        // Angular Material error tips display with dynamic spacing.
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic'
            }
        },

        // DatePipe used only to obtain week day with one letter 'EEEEE'
        {
            provide: DatePipe
        },
    ],
};
// Register languages for DatePipe
registerLocaleData(es);
registerLocaleData(fr);
registerLocaleData(pt);
